import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { Contact } from '../../models/contact';

interface ConfirmDialogProps {
  show: boolean;
  type: 'edit' | 'delete';
  contact: Contact | null;
  onConfirm: (contact: Contact) => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  show,
  type,
  contact,
  onConfirm,
  onCancel,
}) => {
  const [editedContact, setEditedContact] = useState<Contact>({
    id: 0,
    name: '',
    email: '',
    message: '',
  });
  const [isValid, setIsValid] = useState<boolean>(true);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
  }>({});

  // お問い合わせデータを更新
  useEffect(() => {
    if (contact) {
      setEditedContact(contact);
      setErrors({});
      setIsValid(true);
    }
  }, [contact]);

  // バリデーション
  const validateForm = (): boolean => {
    const newErrors: { name?: string; email?: string; message?: string } = {};

    if (!editedContact.name.trim()) {
      newErrors.name = '名前を入力してください';
    } else if (editedContact.name.length < 2) {
      newErrors.name = '名前は2文字以上で入力してください';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editedContact.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!emailRegex.test(editedContact.email)) {
      newErrors.email = '正しいメールアドレス形式で入力してください';
    }

    setErrors(newErrors);
    const valid = Object.keys(newErrors).length === 0;
    setIsValid(valid);
    return valid;
  };

  const handleConfirm = (): void => {
    if (type === 'edit' && !validateForm()) {
      return;
    }
    onConfirm(type === 'edit' ? editedContact : contact!);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newName = e.target.value;
    setEditedContact({ ...editedContact, name: newName });

    // リアルタイムバリデーション
    if (errors.name && newName.trim() && newName.length >= 2) {
      setErrors({ ...errors, name: undefined });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newEmail = e.target.value;
    setEditedContact({ ...editedContact, email: newEmail });

    // リアルタイムバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (errors.email && newEmail.trim() && emailRegex.test(newEmail)) {
      setErrors({ ...errors, email: undefined });
    }
  };

  const handleMessageChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const newMessage = e.target.value;
    setEditedContact({ ...editedContact, message: newMessage });

    // リアルタイムバリデーション
    if (errors.message && newMessage.trim() && newMessage.length >= 2) {
      setErrors({ ...errors, message: undefined });
    }
  };

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      size="lg"
      className="user-dialog"
      backdrop="static"
      keyboard={false}
      animation={true}
      dialogClassName="modal-dialog-centered"
    >
      <Modal.Header
        closeButton
        className={
          type === 'edit' ? 'bg-primary text-white' : 'bg-danger text-white'
        }
      >
        <Modal.Title>
          <i
            className={`fas ${type === 'edit' ? 'fa-user-edit' : 'fa-user-times'} me-2`}
          ></i>
          {type === 'edit' ? 'お問い合わせ情報の編集' : 'お問い合わせの削除'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {type === 'edit' ? (
          <div className="edit-form">
            <div className="user-profile-header mb-4 p-3 rounded">
              <div className="d-flex align-items-center">
                <div className="user-avatar-large me-3">
                  <i className="fas fa-user-circle fa-3x"></i>
                </div>
                <div>
                  <h6 className="mb-1">お問い合わせ情報を更新</h6>
                  <small>ID: #{contact?.id}</small>
                </div>
              </div>
            </div>

            <Form>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  <i className="fas fa-user me-2"></i>名前
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-at"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={editedContact.name || ''}
                    onChange={handleNameChange}
                    isInvalid={!!errors.name}
                    placeholder="名前を入力してください"
                    className={
                      errors.name
                        ? 'is-invalid'
                        : editedContact.name.length >= 2
                          ? 'is-valid'
                          : ''
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  <i className="fas fa-envelope me-2"></i>メールアドレス
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-envelope"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    value={editedContact.email || ''}
                    onChange={handleEmailChange}
                    isInvalid={!!errors.email}
                    placeholder="example@domain.com"
                    className={
                      errors.email
                        ? 'is-invalid'
                        : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedContact.email)
                          ? 'is-valid'
                          : ''
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  <i className="fas fa-envelope me-2"></i>お問い合わせ内容
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-envelope"></i>
                  </InputGroup.Text>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editedContact.message || ''}
                    onChange={handleMessageChange}
                    isInvalid={!!errors.message}
                    placeholder="お問い合わせ内容を入力してください"
                    className={
                      errors.message
                        ? 'is-invalid'
                        : editedContact.message.length >= 2
                          ? 'is-valid'
                          : ''
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Form>
          </div>
        ) : (
          <div className="delete-confirmation">
            <div className="text-center mb-4">
              <i className="fas fa-exclamation-triangle fa-4x mb-3"></i>
              <h5>危険な操作：お問い合わせ削除</h5>
            </div>

            <Alert variant="danger" className="mb-4">
              <Alert.Heading>
                <i className="fas fa-warning me-2"></i>重要な警告
              </Alert.Heading>
              <p className="mb-0">
                この操作は取り消すことができません。削除されたお問い合わせのデータは完全に失われます。
              </p>
            </Alert>

            <div className="user-info-card p-4 rounded mb-4">
              <div className="d-flex align-items-center">
                <i className="fas fa-user-circle fa-3x me-3"></i>
                <div>
                  <h6 className="mb-2">削除対象お問い合わせ</h6>
                  <div className="mb-1">
                    <strong>名前:</strong> {contact?.name}
                  </div>
                  <div className="mb-1">
                    <strong>メールアドレス:</strong> {contact?.email}
                  </div>
                  <div>
                    <strong>お問い合わせID:</strong> #{contact?.id}
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="fw-bold text-danger mb-0">
                上記のお問い合わせを完全に削除しますか？
              </p>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="outline-secondary" onClick={onCancel} size="lg">
          <i className="fas fa-times me-2"></i>キャンセル
        </Button>
        <Button
          variant={type === 'edit' ? 'primary' : 'danger'}
          onClick={handleConfirm}
          size="lg"
          disabled={type === 'edit' && !isValid}
        >
          <i
            className={`fas ${type === 'edit' ? 'fa-save' : 'fa-trash'} me-2`}
          ></i>
          {type === 'edit' ? '変更を保存' : '完全に削除'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;
